import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './LoanDataDisplay.scss';

interface OrganizationLoanReleaseReportItem {
  serialNumber: number;
  date: string | null;
  branch: string;
  loanNumber: string;
  customerName: string;
  totalAmount: number;
  principal: number;
  interest: number;
  numberOfDays: number;
  loanType: string;
  closureType: string;
  principalOnline: number;
  principalOffline: number;
  interestOnline: number;
  interestOffline: number;
  reducingInterest: number;
  grossWeight: number;
  netWeight: number;
  releaseAverage: number;
}

interface OrganizationLoanReleaseReportProps {
  data: OrganizationLoanReleaseReportItem[];
}

const formatDate = (val: string | null) => {
  if (val === null || val === undefined) return '-';
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatCurrency = (val: number) => `₹${(val ?? 0).toLocaleString('en-IN')}`;

const formatText = (val: string) => (val != null && val.length > 0 ? val : '-');

const columns: ColumnsType<OrganizationLoanReleaseReportItem> = [
  {
    title: 'S.No',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    width: 60,
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 120,
    className: 'date-cell',
    render: formatDate,
  },
  {
    title: 'Branch',
    dataIndex: 'branch',
    key: 'branch',
  },
  {
    title: 'Loan Number',
    dataIndex: 'loanNumber',
    key: 'loanNumber',
    render: (val: string) => <strong>{val}</strong>,
  },
  {
    title: 'Customer Name',
    dataIndex: 'customerName',
    key: 'customerName',
  },
  {
    title: 'Total Amount',
    dataIndex: 'totalAmount',
    key: 'totalAmount',
    render: formatCurrency,
  },
  {
    title: 'Principal',
    dataIndex: 'principal',
    key: 'principal',
    render: formatCurrency,
  },
  {
    title: 'Interest',
    dataIndex: 'interest',
    key: 'interest',
    render: formatCurrency,
  },
  {
    title: 'No of Days',
    dataIndex: 'numberOfDays',
    key: 'numberOfDays',
  },
  {
    title: 'Type',
    dataIndex: 'loanType',
    key: 'loanType',
    filters: [
      { text: 'Direct', value: 'Direct' },
      { text: 'Takeover', value: 'Takeover' },
      { text: 'Repledge', value: 'Repledge' },
    ],
    onFilter: (value, record) => record.loanType === String(value),
    render: formatText,
  },
  {
    title: 'Closure Type',
    dataIndex: 'closureType',
    key: 'closureType',
    render: formatText,
  },
  {
    title: 'Principal Online',
    dataIndex: 'principalOnline',
    key: 'principalOnline',
    render: formatCurrency,
  },
  {
    title: 'Principal Offline',
    dataIndex: 'principalOffline',
    key: 'principalOffline',
    render: formatCurrency,
  },
  {
    title: 'Interest Online',
    dataIndex: 'interestOnline',
    key: 'interestOnline',
    render: formatCurrency,
  },
  {
    title: 'Interest Offline',
    dataIndex: 'interestOffline',
    key: 'interestOffline',
    render: formatCurrency,
  },
  {
    title: 'Reducing Interest',
    dataIndex: 'reducingInterest',
    key: 'reducingInterest',
    render: formatCurrency,
  },
  {
    title: 'GW (g)',
    dataIndex: 'grossWeight',
    key: 'grossWeight',
    render: (val: number) => `${(val ?? 0).toFixed(3)} g`,
  },
  {
    title: 'NW (g)',
    dataIndex: 'netWeight',
    key: 'netWeight',
    render: (val: number) => `${(val ?? 0).toFixed(3)} g`,
  },
  {
    title: 'Release Average (₹/g)',
    dataIndex: 'releaseAverage',
    key: 'releaseAverage',
    render: formatCurrency,
  },
];

const OrganizationLoanReleaseReportDisplay: React.FC<
OrganizationLoanReleaseReportProps
> = ({ data }) => {
  // Totals cover the whole report, not just the visible page.
  const totals = useMemo(
    () =>
      data.reduce(
        (acc, item) => ({
          totalAmount: acc.totalAmount + (item.totalAmount ?? 0),
          principal: acc.principal + (item.principal ?? 0),
          interest: acc.interest + (item.interest ?? 0),
          principalOnline: acc.principalOnline + (item.principalOnline ?? 0),
          principalOffline: acc.principalOffline + (item.principalOffline ?? 0),
          interestOnline: acc.interestOnline + (item.interestOnline ?? 0),
          interestOffline: acc.interestOffline + (item.interestOffline ?? 0),
          reducingInterest: acc.reducingInterest + (item.reducingInterest ?? 0),
          grossWeight: acc.grossWeight + (item.grossWeight ?? 0),
          netWeight: acc.netWeight + (item.netWeight ?? 0),
        }),
        {
          totalAmount: 0,
          principal: 0,
          interest: 0,
          principalOnline: 0,
          principalOffline: 0,
          interestOnline: 0,
          interestOffline: 0,
          reducingInterest: 0,
          grossWeight: 0,
          netWeight: 0,
        },
      ),
    [data],
  );

  // Release average is a rate, so the total row shows the portfolio-wide rate rather than a sum.
  const overallAverage =
    totals.netWeight > 0 ? totals.totalAmount / totals.netWeight : 0;

  return (
    <div className="loan-data-container">
      <h2>Organization Loan Release Report</h2>
      <Table
        columns={columns}
        dataSource={data.map((item) => ({
          ...item,
          key: `${item.loanNumber}-${item.serialNumber}`,
        }))}
        rowKey="key"
        pagination={{ pageSize: 20, showTotal: (t) => `${t} records` }}
        scroll={{ x: true }}
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="totals-row">
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                <strong>{formatCurrency(totals.totalAmount)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                <strong>{formatCurrency(totals.principal)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                <strong>{formatCurrency(totals.interest)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={9}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={10}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={11}>
                <strong>{formatCurrency(totals.principalOnline)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={12}>
                <strong>{formatCurrency(totals.principalOffline)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={13}>
                <strong>{formatCurrency(totals.interestOnline)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={14}>
                <strong>{formatCurrency(totals.interestOffline)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={15}>
                <strong>{formatCurrency(totals.reducingInterest)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={16}>
                <strong>{totals.grossWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={17}>
                <strong>{totals.netWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={18}>
                <strong>₹{overallAverage.toFixed(2)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default OrganizationLoanReleaseReportDisplay;
